import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { valueKey: 'hero.stat1.value', labelKey: 'hero.stat1.label' },
    { valueKey: 'hero.stat2.value', labelKey: 'hero.stat2.label' },
    { valueKey: 'hero.stat3.value', labelKey: 'hero.stat3.label' },
    { valueKey: 'hero.stat4.value', labelKey: 'hero.stat4.label' },
  ];

  const base = 'transition-all duration-700 ease-out';
  const visible = 'opacity-100 translate-y-0';
  const hidden = 'opacity-0 translate-y-6';

  return (
    <section id="home" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/f2e80cb9-6e9d-476e-b707-141660fae558/4d1ea3bd-2fd2-4857-80c6-1c2e3d5beea7_8cbb24ef-7fbb-4b1e-ab9e-7d12ff827151.png?v=1a27e68d0e5241bb3564f1988305dd24"
          alt="ONOPTO Semiconductor Light Source Laboratory"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d1f3c]/80 via-[#1B3F7A]/60 to-[#0d1f3c]/75" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-32 pb-20">
        {/* Badge */}
        <div className={`${base} ${mounted ? visible : hidden} inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm mb-8`}
          style={{ transitionDelay: '0ms' }}>
          <span className="w-2 h-2 rounded-full bg-[#7eb8f7] animate-pulse inline-block" />
          <span className="text-white/90 text-sm tracking-widest uppercase font-medium">{t('hero.badge')}</span>
        </div>

        {/* Title */}
        <h2
          className={`${base} text-5xl md:text-7xl font-black text-white leading-tight mb-6 max-w-3xl`}
          style={{ transitionDelay: '120ms' }}
        >
          <span className={`block ${base} ${mounted ? visible : hidden}`} style={{ transitionDelay: '120ms' }}>
            {t('hero.title1')}
          </span>
          <span className={`block text-[#7eb8f7] ${base} ${mounted ? visible : hidden}`} style={{ transitionDelay: '240ms' }}>
            {t('hero.title2')}
          </span>
        </h2>

        {/* Description */}
        <p
          className={`${base} text-white/75 text-lg md:text-xl max-w-xl leading-relaxed mb-10 ${mounted ? visible : hidden}`}
          style={{ transitionDelay: '360ms' }}
        >
          {t('hero.desc')}
        </p>

        {/* Buttons */}
        <div
          className={`${base} flex flex-col sm:flex-row gap-4 ${mounted ? visible : hidden}`}
          style={{ transitionDelay: '480ms' }}
        >
          <button
            onClick={() => scrollTo('services')}
            className="px-8 py-3.5 bg-white text-[#1B3F7A] font-semibold rounded-full hover:bg-gray-100 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            {t('hero.btn1')}
          </button>
          <button
            onClick={() => scrollTo('portfolio')}
            className="flex items-center gap-2 px-8 py-3.5 border border-white/40 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            {t('hero.btn2')}
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>

        {/* Stats */}
        <div
          className={`${base} mt-20 flex flex-wrap gap-10 ${mounted ? visible : hidden}`}
          style={{ transitionDelay: '600ms' }}
        >
          {stats.map(stat => (
            <div key={stat.labelKey}>
              <div className="text-3xl md:text-4xl font-black text-white">{t(stat.valueKey)}</div>
              <div className="text-white/60 text-sm mt-1 tracking-wide">{t(stat.labelKey)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs tracking-widest uppercase">{t('hero.scroll')}</span>
        <i className="ri-arrow-down-line text-white/50 text-lg" />
      </div>
    </section>
  );
}
